/**
 * PDF loading and page rasterisation via pdf.js.
 *
 * The worker is bundled by Vite (`?url`) rather than fetched from a CDN, for
 * the same reasons as the IFC WASM. See CODE_RULES.md §7.
 */
import * as pdfjs from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { PDF_VIEW } from "../core/config";
import { appError, describeUnknown, err, ok, type Result } from "../core/result";
import { logger } from "../core/logger";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export interface PdfHandle {
  readonly pageCount: number;
  /** Renders a 1-indexed page into the canvas at the given zoom. */
  renderPage(pageNumber: number, canvas: HTMLCanvasElement, scale: number): Promise<void>;
  dispose(): Promise<void>;
}

export async function openPdf(bytes: Uint8Array): Promise<Result<PdfHandle>> {
  try {
    // pdf.js takes ownership of the buffer, so hand it a copy — the caller's
    // Uint8Array would otherwise be detached out from under them.
    const task = pdfjs.getDocument({ data: new Uint8Array(bytes) });
    const document = await task.promise;

    return ok({
      pageCount: document.numPages,
      async renderPage(pageNumber, canvas, scale) {
        const clamped = Math.min(Math.max(scale, PDF_VIEW.MIN_SCALE), PDF_VIEW.MAX_SCALE);
        const page = await document.getPage(pageNumber);
        const viewport = page.getViewport({ scale: clamped });
        const context = canvas.getContext("2d");
        if (context === null) throw new Error("2D canvas context unavailable");

        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        await page.render({ canvas, canvasContext: context, viewport }).promise;
      },
      async dispose() {
        // destroy() lives on the loading task, not the document proxy; it also
        // tears down the worker, which document.cleanup() does not.
        await task.destroy();
      },
    });
  } catch (cause) {
    logger.error("PDF parse failed", cause);
    return err(
      appError(
        "PDF_PARSE_FAILED",
        "That PDF could not be opened. It may be corrupt or password-protected.",
        describeUnknown(cause),
      ),
    );
  }
}
